import { Injectable } from '@angular/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { CookieService } from 'ngx-cookie-service';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
  AUTH_PROVIDER_HINT_PARAM,
  AUTH_PROVIDER_STORAGE_KEY,
  AuthProviderId,
  AuthProviderSettings,
  chooseProvider,
  defaultProvider,
  enabledProviders,
  isEnabledProvider,
  providerSettings,
  readJwt,
} from './auth-providers';

/** Cookies and cache that describe the SP user of one sign-in system; cleared when the system changes. */
const SP_IDENTITY_COOKIES = [
  'userId',
  'customerId',
  'externalUserGUID',
  'businessid',
  'oneLoginUserId',
  'isLoggedin',
];
const SP_AUTH_CONTEXT_CACHE_KEY = 'spAuthContextLoadedAt';
/** `{system}:{token subject}` of the user the SP identity cookies were written for. */
const SP_COOKIE_OWNER = 'spIdentityOwner';

export interface AuthCheckResult {
  provider: AuthProviderId;
  isAuthenticated: boolean;
  accessToken?: string;
}

/**
 * The signed-in session, whichever system issued it (OneLogin or Identity).
 * Components use this instead of OidcSecurityService so every call names the active configuration.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private active: AuthProviderId;
  private readonly hint: AuthProviderId | null;

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private cookieService: CookieService
  ) {
    // Read once, on the first navigation (the router has not rewritten the URL yet).
    this.hint = this.readHint();
    const remembered = this.readRemembered();
    this.active = isEnabledProvider(this.hint)
      ? this.hint
      : isEnabledProvider(remembered)
        ? remembered
        : defaultProvider();
  }

  get provider(): AuthProviderId {
    return this.active;
  }

  get settings(): AuthProviderSettings {
    return providerSettings(this.active);
  }

  get homeUrl(): string {
    return this.settings.homeUrl;
  }

  get loginUrl(): string {
    return this.settings.loginUrl;
  }

  get profileUrl(): string {
    return this.settings.profileUrl;
  }

  /**
   * Runs the library check for every enabled system (no network unless a callback or a renewal
   * is due), then settles the active one:
   * - OIDC callback: the system whose stored `state` matches (the library picks it);
   * - otherwise: link hint, remembered system with a session, any session, remembered, default.
   */
  checkAuth(): Observable<AuthCheckResult> {
    const onCallback = this.isOidcCallbackUrl();
    return this.oidcSecurityService.checkAuthMultiple().pipe(
      take(1),
      map((responses: LoginResponse[]) => {
        const results = (responses || []).filter((r) => isEnabledProvider(r?.configId));
        if (onCallback) {
          const current = results[0];
          const provider = isEnabledProvider(current?.configId)
            ? current.configId
            : this.active;
          this.use(provider);
          return {
            provider,
            isAuthenticated: !!current?.isAuthenticated,
            accessToken: current?.accessToken,
          };
        }

        const authenticated = results
          .filter((r) => r.isAuthenticated)
          .map((r) => r.configId as AuthProviderId);
        const provider = chooseProvider(this.hint, this.readRemembered(), authenticated);
        this.use(provider);

        // A link that names another system ends the other local session, so two users never mix.
        authenticated
          .filter((id) => id !== provider)
          .forEach((id) => this.oidcSecurityService.logoffLocal(id));

        const chosen = results.find((r) => r.configId === provider);
        return {
          provider,
          isAuthenticated: !!chosen?.isAuthenticated,
          accessToken: chosen?.isAuthenticated ? chosen.accessToken : undefined,
        };
      })
    );
  }

  accessToken$(): Observable<string> {
    return this.oidcSecurityService.getAccessToken(this.active).pipe(
      take(1),
      catchError(() => of(''))
    );
  }

  /**
   * Profile claims for the header and forms. OneLogin: the id_token claims (as before).
   * Identity: the access-token claims win, because that is where Identity puts email, company type,
   * Chargebee id and the 3.0 GUID (the Identity-only build decoded the access token directly).
   */
  userData$(): Observable<any> {
    const provider = this.active;
    const userData$ = this.oidcSecurityService.getUserData(provider).pipe(take(1));
    if (provider !== 'identity') {
      return userData$;
    }
    return forkJoin([userData$, this.accessToken$()]).pipe(
      map(([userData, token]) => ({
        ...(userData || {}),
        ...(readJwt(token)?.payload ?? {}),
      }))
    );
  }

  /** Starts sign-in with the active system (or the one given). */
  login(provider: AuthProviderId = this.active): void {
    this.use(provider);
    this.oidcSecurityService.authorize(provider);
  }

  /** Signs out of the active system; `done` runs once the local state is gone. */
  logout(done?: () => void): void {
    const provider = this.active;
    this.oidcSecurityService
      .logoff(provider)
      .pipe(take(1))
      .subscribe({
        next: () => done?.(),
        error: () => done?.(),
      });
  }

  /**
   * The SP identity cookies (customer id, 3.0 GUID, ...) are shared by every tab, while the choice of
   * system and the tokens are per tab. Before this tab uses them, make sure they were written for
   * this tab's user; otherwise drop them so the token claims and `auth/context` write them again.
   */
  claimSpIdentityCookies(provider: AuthProviderId, token: string): void {
    const claims = readJwt(token)?.payload ?? {};
    const owner = `${provider}:${String(claims['sub'] ?? claims['userId'] ?? '')}`;
    if (this.cookieService.get(SP_COOKIE_OWNER) === owner) {
      return;
    }
    SP_IDENTITY_COOKIES.forEach((name) => this.cookieService.delete(name, '/'));
    sessionStorage.removeItem(SP_AUTH_CONTEXT_CACHE_KEY);
    this.cookieService.set(SP_COOKIE_OWNER, owner, undefined, '/');
  }

  /** Keeps this tab's system across `sessionStorage.clear()` (the guard clears storage before re-auth). */
  rememberAfterStorageClear(): void {
    this.writeRemembered(this.active);
  }

  isOidcCallbackUrl(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  }

  private use(provider: AuthProviderId): void {
    if (!isEnabledProvider(provider)) {
      return;
    }
    const previous = this.readRemembered();
    if (previous && previous !== provider) {
      // SP identity cookies belong to the previous system's user.
      SP_IDENTITY_COOKIES.forEach((name) => this.cookieService.delete(name, '/'));
      sessionStorage.removeItem(SP_AUTH_CONTEXT_CACHE_KEY);
    }
    this.active = provider;
    this.writeRemembered(provider);
  }

  private readHint(): AuthProviderId | null {
    try {
      const value = new URLSearchParams(window.location.search)
        .get(AUTH_PROVIDER_HINT_PARAM)
        ?.trim()
        .toLowerCase();
      return isEnabledProvider(value) ? value : null;
    } catch {
      return null;
    }
  }

  private readRemembered(): AuthProviderId | null {
    try {
      const value = sessionStorage.getItem(AUTH_PROVIDER_STORAGE_KEY);
      return isEnabledProvider(value) ? value : null;
    } catch {
      return null;
    }
  }

  private writeRemembered(provider: AuthProviderId): void {
    try {
      if (enabledProviders().includes(provider)) {
        sessionStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, provider);
      }
    } catch {
      // Storage blocked: the environment default applies next time.
    }
  }
}
